import { FormattedHTMLMessage } from "react-intl";

import { useFormSelectChoice } from "@/components/FormInputs/InputSelect/InputSelectComponent";

import { type CollectInfoSteps, VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import { resetRefs } from "../../lib/refs/refs";
import {
  type VerificationService,
  type FormSelectChoice,
  type Organization,
  type MilitaryPersonalInfoViewModel,
  type ActiveMilitaryPersonalInfoResponse,
  type InactiveMilitaryPersonalInfoResponse,
  type MilitaryStatusResponse,
  type CustomFormFieldConfig,
  type FieldDescription,
  type State,
  type MilitaryStatus,
} from "../../lib/types/types";
import { useCollectFieldsEndpoint } from "../../lib/ServerApi/useCollectFieldsEndpoint";
import { isCountrySelectionNeeded } from "../FormFields/Country/countryUtils";
import {
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
  getFieldConfigByFieldId,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";

import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { StatusComponent as MilitaryStatusComponent } from "../FormFields/Status/StatusComponent";
import { BranchOfServiceComponent as BranchOfService } from "../FormFields/BranchOfService/BranchOfServiceComponent";
import { DischargeDateComponent as DischargeDate } from "../FormFields/DischargeDate/DischargeDateComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { ActiveDutyStartDateComponent } from "../FormFields/ActiveDutyStartDate/ActiveDutyStartDateComponent";
import { AddressComponent } from "../FormFields/Address/AddressComponent";
import { CityComponent } from "../FormFields/City/City";
import { StateSelectComponent } from "../FormFields/State/StateSelectComponent";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { CaptchaComponent } from "../FormFields/Captcha/CaptchaComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { getOptions } from "../../options/options";

import { updateMilitaryViewModel } from "./militaryStepHelpers";

interface Props {
  verificationService: VerificationService;
}

export default function StepCollectMilitaryPersonalInfoComponent({ verificationService }: Props) {
  const viewModel = verificationService.viewModel as MilitaryPersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse = verificationService.verificationResponse as
    | ActiveMilitaryPersonalInfoResponse
    | InactiveMilitaryPersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

  // Seed: show dischargeDate on first paint when the response indicates an
  // inactive flow, or when the program's transitional validity window applies.
  // The /fields response replaces this once it resolves.
  const showDischargeDateInitially =
    verificationResponse.currentStep ===
      VerificationStepsEnum.collectInactiveMilitaryPersonalInfo ||
    !!verificationService.programTheme.transitionalMilitaryValidityDays;
  const initialRequiredFields: FieldDescription[] = showDischargeDateInitially
    ? [{ key: "dischargeDate" }]
    : [];

  const {
    availableStatuses: dynamicStatuses,
    isFieldRequired,
    isFieldOptional,
  } = useCollectFieldsEndpoint({
    verificationId: verificationResponse.verificationId,
    currentStep: VerificationStepsEnum.collectMilitaryPersonalInfo,
    viewModel,
    defaultRequiredFields: initialRequiredFields,
  });

  const countryChoiceValue = viewModel.countryChoice?.value;
  const needToPickCountry = isCountrySelectionNeeded(
    verificationService.programTheme,
    countryChoiceValue,
  );

  const statusOptions = useFormSelectChoice(
    (dynamicStatuses ??
      (verificationResponse as unknown as MilitaryStatusResponse).availableStatuses ??
      (verificationService.previousVerificationResponse as MilitaryStatusResponse)
        ?.availableStatuses) as MilitaryStatus[],
  );

  const locale = viewModel.localeChoice.value;
  const options = getOptions();

  const showDischargeDate = isFieldRequired("dischargeDate") || isFieldOptional("dischargeDate");

  const updateMilitary = (key: keyof MilitaryPersonalInfoViewModel, value: any) => {
    verificationService.updateViewModel(
      updateMilitaryViewModel(key, value, verificationService, statusOptions),
    );
  };

  resetRefs();

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
      className="sid-military-personal-info-container"
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="active-military-personal-info"
      />
      <>
        <ChangeLocaleV2Component verificationService={verificationService} />
        <CountryComponentWrapper
          verificationService={verificationService}
          disabled={getFieldConfigByFieldId("country")?.disabled}
        />
        {statusOptions.length > 0 ? (
          <MilitaryStatusComponent
            segmentId="military"
            value={statusOptions.find((option) => option.value === viewModel.status)}
            isErrored={!!fieldValidationErrors.status}
            errorId="invalidMilitaryStatus"
            options={statusOptions}
            disabled={needToPickCountry || getFieldConfigByFieldId("status")?.disabled}
            explanation={
              needToPickCountry ? (
                <FormattedHTMLMessage id="chooseCountry" defaultMessage="Choose a country first" />
              ) : undefined
            }
            onChange={(status: FormSelectChoice) => {
              updateMilitary("status", status ? (status.value as string) : "");
              updateFieldValidationErrorsByFieldId(
                "status",
                status ? (status.value as string) : "",
                verificationService,
              );
            }}
          />
        ) : null}
        <BranchOfService
          isErrored={!!fieldValidationErrors.organization}
          onChange={(choice: Organization) => {
            updateViewModelOrganization(choice, verificationService);
            updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
          }}
          value={{
            value: viewModel.organization.id,
            label: viewModel.organization.name,
          }}
          verificationService={verificationService}
          disabled={getFieldConfigByFieldId("organization")?.disabled}
        />

        <div className="sid-names">
          <FirstName
            value={viewModel.firstName}
            isErrored={!!fieldValidationErrors.firstName}
            disabled={getFieldConfigByFieldId("firstName")?.disabled}
            onChange={(newValue) => {
              updateMilitary("firstName", newValue);
              updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
            }}
          />
          <LastName
            value={viewModel.lastName}
            isErrored={!!fieldValidationErrors.lastName}
            disabled={getFieldConfigByFieldId("lastName")?.disabled}
            onChange={(newValue) => {
              updateMilitary("lastName", newValue);
              updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
            }}
          />
        </div>

        <BirthDate
          errorId={fieldValidationErrors.birthDate}
          locale={locale}
          onChange={(newValue) => {
            updateMilitary("birthDate", newValue);
            updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
          }}
          value={viewModel.birthDate}
          disabled={getFieldConfigByFieldId("birthDate")?.disabled}
        />

        {showDischargeDate && (
          <DischargeDate
            mode={
              verificationService.programTheme.transitionalMilitaryValidityDays ? "date" : "month"
            }
            errorId={fieldValidationErrors.dischargeDate}
            onChange={(newValue) => {
              updateMilitary("dischargeDate", newValue);
              updateFieldValidationErrorsByFieldId("dischargeDate", newValue, verificationService);
            }}
            value={viewModel.dischargeDate}
            locale={locale}
            disabled={getFieldConfigByFieldId("dischargeDate")?.disabled}
            isRequired={isFieldRequired("dischargeDate")}
          />
        )}

        {verificationService.programTheme.strictMilitaryValidationEnabled && (
          <ActiveDutyStartDateComponent
            errorId={fieldValidationErrors.activeDutyStartDate}
            locale={locale}
            onChange={(newValue) => {
              updateMilitary("activeDutyStartDate", newValue);
              updateFieldValidationErrorsByFieldId(
                "activeDutyStartDate",
                newValue,
                verificationService,
              );
            }}
            value={viewModel.activeDutyStartDate}
            disabled={getFieldConfigByFieldId("activeDutyStartDate")?.disabled}
            isRequired={getFieldConfigByFieldId("activeDutyStartDate")?.requireIfOptional}
          />
        )}

        <Email
          value={viewModel.email}
          isErrored={!!fieldValidationErrors.email}
          explanation={
            <FormattedHTMLMessage
              id="emailExplanation"
              defaultMessage="Personal email address is recommended"
            />
          }
          onChange={(newValue) => {
            updateMilitary("email", newValue);
            updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
          }}
          onKeyDown={(event) => handleEmailOnKeyDown(event)}
          disabled={getFieldConfigByFieldId("email")?.disabled}
        />

        {verificationService.programTheme.isSmsNotifierConfigured ||
        verificationService.programTheme.smsLoopEnabled ? (
          <PhoneNumber
            isRequired={!!verificationService.programTheme.smsLoopEnabled}
            value={viewModel.phoneNumber}
            isErrored={!!fieldValidationErrors.phoneNumber}
            onChange={(newValue) => {
              updateMilitary("phoneNumber", newValue);
              updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
            }}
            selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
            disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
          />
        ) : null}

        {isFieldOptional("address1") && (
          <AddressComponent
            value={viewModel.address1}
            isErrored={!!fieldValidationErrors.address1}
            errorId={fieldValidationErrors.address1}
            disabled={getFieldConfigByFieldId("address1")?.disabled}
            onChange={(value) => {
              updateMilitary("address1", value);
              updateFieldValidationErrorsByFieldId("address1", value, verificationService);
            }}
            isRequired={getFieldConfigByFieldId("address1")?.requireIfOptional}
          />
        )}
        {isFieldOptional("city") && (
          <CityComponent
            value={viewModel.city}
            isErrored={!!fieldValidationErrors.city}
            errorId={fieldValidationErrors.city}
            disabled={getFieldConfigByFieldId("city")?.disabled}
            onChange={(value) => {
              updateMilitary("city", value);
              updateFieldValidationErrorsByFieldId("city", value, verificationService);
            }}
            isRequired={getFieldConfigByFieldId("city")?.requireIfOptional}
          />
        )}
        {isFieldOptional("state") && (
          <StateSelectComponent
            value={viewModel.state || undefined}
            isErrored={Boolean(fieldValidationErrors.state)}
            onChange={async (stateChoice: FormSelectChoice<State, string>) => {
              const stateValue = stateChoice ? stateChoice.value : undefined;
              updateMilitary("state", stateValue);
              updateFieldValidationErrorsByFieldId("state", stateValue, verificationService);
            }}
            label={<FormattedHTMLMessage id="state" defaultMessage="State" />}
            disabled={getFieldConfigByFieldId("state")?.disabled}
            isRequired={getFieldConfigByFieldId("state")?.requireIfOptional}
          />
        )}
        {isFieldOptional("postalCode") && (
          <PostalCodeComponent
            required={getFieldConfigByFieldId("postalCode")?.requireIfOptional}
            isErrored={!!fieldValidationErrors.postalCode}
            onChange={(newValue) => {
              updateMilitary("postalCode", newValue);
              updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
            }}
            value={viewModel.postalCode}
            disabled={getFieldConfigByFieldId("postalCode")?.disabled}
          />
        )}

        {options.customFormFields.map((config: CustomFormFieldConfig) => (
          <FormFieldCustom config={config} verificationService={verificationService} />
        ))}

        <MarketConsentWrapper
          verificationService={verificationService}
          isErrored={!!fieldValidationErrors.marketConsentValue}
          onChange={(newValue) => {
            updateMilitary("metadata", {
              ...verificationService.viewModel.metadata,
              marketConsentValue: newValue,
            });
            updateFieldValidationErrorsByFieldId(
              "marketConsentValue",
              newValue,
              verificationService,
            );
          }}
          viewModel={viewModel}
          disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
        />

        <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

        <CaptchaComponent
          verificationService={verificationService}
          isErrored={!!fieldValidationErrors.captchaToken}
        />

        <PersonalInfoStepSubmitButton
          step={verificationResponse.currentStep as CollectInfoSteps}
          verificationService={verificationService}
          viewModel={viewModel}
          failedInstantMatch={failedInstantMatch}
          buttonText={
            <FormattedHTMLMessage
              id="step.personalInfo.submitButtonText"
              defaultMessage="Verify My Military Status"
            />
          }
        />
      </>
    </PersonalInfoWrapper>
  );
}
